import S from 'common/service/sanctuary';
import { intersperse, contains, replace } from 'ramda';
import { createSelector } from 'reselect';

import {
  EMPTY,
  NOT_EMPTY,
  EQUALS,
  NOT_EQUALS,
  LESS_THAN,
  GREATER_THAN,
  BETWEEN,
  WITHIN,
} from '../constraint/operators';

import {
  PLAIN,
  CHOICE,
  DATETIME,
  LOCATION,
} from './types';

import { getConstraintValue } from '../constraint/selectors';

const getConstraints = S.get(S.is(Array), 'constraints');
const getType = S.get(S.is(String), 'type');
const getProperty = S.get(S.is(String), 'property');
export const getDefaultOperator = createSelector(
  getType,
  S.maybe(EQUALS, type => ({
    [LOCATION]: WITHIN,
    [DATETIME]: LESS_THAN,
  })[type] || EQUALS),
);

export const hasConstraints = createSelector(
  getConstraints,
  S.pipe([
    S.fromMaybe([]),
    S.map(getConstraintValue),
    S.justs,
    S.prop('length'),
    Boolean,
  ]),
);

const ISODate = date => new Date(date).toISOString().slice(0, -1);

const valueToDSL = (type, value) => ({
  [PLAIN]: () => `'${value}'`,
  [CHOICE]: () => `'${value}'`,
  [DATETIME]: () => (value.length > 1
    ? [ISODate(value[0]), ISODate(value[1])]
    : ISODate(value[0])
  ),
})[type]();

export const toDSL = createSelector(
  getConstraints,
  getType,
  getProperty,
  (...args) => S.join(S.lift3(
    constraints => type => property => S.pipe([
      S.map(constraint => S.chain(
        value => S.toMaybe(({
          [EMPTY]: () => `${property} is null`,
          [NOT_EMPTY]: () => `not (${property} is null)`,
          [EQUALS]: () => (
            contains('*', value)
              ? `${property} LIKE '${replace(/\*/g, '%', value)}'`
              : `${property} = ${valueToDSL(type, value)}`
          ),
          [NOT_EQUALS]: () => (
            contains('*', value)
              ? `not ${property} LIKE '${replace(/\*/g, '%', value)}'`
              : `${property} != ${valueToDSL(type, value)}`
          ),
          [LESS_THAN]: () => (`${property} < ${valueToDSL(type, value)}`),
          [GREATER_THAN]: () => (`${property} > ${valueToDSL(type, value)}`),
          [BETWEEN]: () => (`${property} > ${valueToDSL(type, value)[0]} and ${property} < ${valueToDSL(type, value)[1]}`),
          [WITHIN]: () => (`distanceMeters(latitude, longitude, ${value[1]}, ${value[2]}) < ${value[0]}`),
        })[constraint.operator]()),
        getConstraintValue(constraint),
      )),
      S.justs,
      chunks => (chunks.length
        ? S.Just(S.unwords(intersperse('or', chunks)))
        : S.Nothing
      ),
    ], constraints),
    ...args,
  )),
);
